import React from "react";
import PropTypes from "prop-types";
import { Segment, Header, Icon, Grid, Progress } from "semantic-ui-react";

const TrendAnalysisChart = ({ searchTrends }) => {
  if (searchTrends.length < 2) return null;

  const totalSearches = searchTrends.reduce((sum, item) => sum + item.total, 0);
  const avgSearches =
    Math.round((totalSearches / searchTrends.length) * 10) / 10;
  const maxSearches = Math.max(...searchTrends.map((item) => item.total));

  const recentActivityItems = Math.min(searchTrends.length, 7);
  const headerHeight = 45;
  const itemHeight = 45;
  const padding = 25;
  const calculatedHeight =
    headerHeight + recentActivityItems * itemHeight + padding;

  const containerHeight = calculatedHeight;

  const labelSpace = 60;
  const chartPadding = 40;
  const maxBarHeight = containerHeight - labelSpace - chartPadding - 40;

  const chartData = searchTrends.map((item, index) => {
    const heightPercentage =
      maxSearches > 0 ? (item.total / maxSearches) * 100 : 0;

    const heightPx =
      maxSearches > 0
        ? Math.max(
            (heightPercentage / 100) * maxBarHeight,
            item.total > 0 ? 8 : 3
          )
        : 3;

    return {
      id: `${item.date}-${index}`, // Create unique ID for key
      date: new Date(item.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      searches: item.total,
      heightPx,
      percentage: Math.round(heightPercentage),
    };
  });

  const barStyle = (item) => ({
    backgroundColor: item.searches > 0 ? "#225745" : "#e0e0e0",
    width: "100%",
    height: `${item.heightPx}px`,
    minHeight: "3px",
    borderRadius: "2px 2px 0 0",
    transition: "all 0.3s ease",
    cursor: "pointer",
    opacity: item.searches > 0 ? 1 : 0.3,
  });

  const getProgressColor = (searches) => {
    if (searches > avgSearches) return "green";
    if (searches > 0) return "yellow";
    return "grey";
  };

  return (
    <Segment>
      <Header as="h3" style={{ color: "#225745", marginBottom: "1.5rem" }}>
        <Icon name="chart line" />
        Search Trends Analysis
      </Header>

      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <Segment
              style={{ height: `${containerHeight}px`, overflow: "hidden" }}
            >
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    marginBottom: "10px",
                    fontSize: "14px",
                    color: "#666",
                    flexShrink: 0,
                  }}
                >
                  <strong>Daily Search Volume</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: `${maxBarHeight + labelSpace}px`,
                    gap: "8px",
                    paddingLeft: "15px",
                    paddingRight: "15px",
                    flex: 1,
                  }}
                >
                  {chartData.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        minWidth: "20px",
                        height: "100%",
                        position: "relative",
                      }}
                    >
                      {/* Container for bar that positions it above the label space */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          height: `${maxBarHeight}px`,
                          justifyContent: "flex-end",
                          width: "100%",
                        }}
                      >
                        <div
                          style={barStyle(item)}
                          title={`${item.date}: ${item.searches} searches`}
                        />
                      </div>
                      {/* Label positioned in the reserved space */}
                      <div
                        style={{
                          height: `${labelSpace}px`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#666",
                            transform: "rotate(-45deg)",
                            whiteSpace: "nowrap",
                            lineHeight: "1",
                          }}
                        >
                          {item.date}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column width={8}>
            <Segment
              style={{ height: `${containerHeight}px`, overflow: "hidden" }}
            >
              <Header
                as="h4"
                style={{ color: "#225745", marginBottom: "1rem" }}
              >
                <Icon name="calendar" />
                Recent Activity (Last 7 Days)
              </Header>
              {chartData.slice(-7).map((item) => (
                <div key={item.id} style={{ marginBottom: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                    }}
                  >
                    <span style={{ fontSize: "0.9rem" }}>{item.date}</span>
                    <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
                      {item.searches} searches
                    </span>
                  </div>
                  <Progress
                    percent={item.percentage}
                    color={getProgressColor(item.searches)}
                    size="tiny"
                  />
                </div>
              ))}
            </Segment>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

TrendAnalysisChart.propTypes = {
  searchTrends: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default TrendAnalysisChart;
